import React from 'react';
import { Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import type { BbchInfo } from '../../../types/simulation';
import {
  bbchConfidenceLabel,
  bbchCropGroupLabel,
  bbchPrincipalLabel,
} from '../../../pages/plantings/planting-details/constants';

const { Text } = Typography;

interface PlantingBbchTagProps {
  bbch?: BbchInfo | null;
  fallback?: React.ReactNode;
}

export const isDisplayablePlantingBbch = (bbch?: BbchInfo | null): boolean => {
  if (!bbch) return false;
  if (bbch.status === 'not_applicable' || bbch.status === 'unmapped') return false;
  return bbch.code != null;
};

const formatBbchTooltip = (bbch: BbchInfo): string => {
  const principal = bbch.principalStage ?? null;
  const principalText = principal != null
    ? (bbchPrincipalLabel(principal) || i18n.t('plantings:bbch.principalCode', { code: principal }))
    : '';
  const parts = [
    principalText,
    bbch.label,
    bbch.cropGroup ? i18n.t('plantings:bbch.group', { value: bbchCropGroupLabel(bbch.cropGroup) || bbch.cropGroup }) : null,
    bbch.confidence ? i18n.t('plantings:bbch.confidence', { value: bbchConfidenceLabel(bbch.confidence) || bbch.confidence }) : null,
  ].filter(Boolean);
  return parts.join('\n');
};

const PlantingBbchTag: React.FC<PlantingBbchTagProps> = ({ bbch, fallback = null }) => {
  useTranslation('plantings');
  if (!isDisplayablePlantingBbch(bbch)) {
    return fallback ? <Text type="secondary">{fallback}</Text> : null;
  }

  const tooltip = formatBbchTooltip(bbch!);
  const tag = <Tag color="geekblue">BBCH {bbch!.code}</Tag>;
  if (!tooltip) return tag;

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}>
      {tag}
    </Tooltip>
  );
};

export default PlantingBbchTag;
